import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ShoppingBag } from 'lucide-react';
import { Header, Footer } from '../Common';
import { SimpleProduct, PackageProduct, Summary } from '@ajat/design-system/ShoppingCart';
import Button from '@ajat/design-system/Button';
import EmptyState from '@ajat/design-system/EmptyState';
import useCartSidebar from '@ajat/design-system/hooks/useCartSidebar';
import { useCart } from '../../../../context/CartContext';
import useSchoolSelection from '../../../../hooks/useSchoolSelection';
import { publicHref } from '../../../../config/deployment';
import '../../Common/ShoppingCart/ShoppingCart.css';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const cart = useCart('clsse');
  const {
    products, packages, subtotal, total,
    discount, discountAmount,
    productsTotal, packagesTotal,
    loading,
    updateItem, removeItem, applyDiscount, removeDiscount,
  } = cart;

  const [discountError, setDiscountError] = useState({ code: '', message: '' });

  const discountPercent = discount ? (discount.type === 'percentage' ? discount.value : 0) : 0;
  // Localized discount label, built from the discount type/value.
  const discountText = discount
    ? (discount.type === 'percentage' ? `${discountPercent}% RABATT` : 'RABATT')
    : '';

  /* ── Discount handlers ── */
  const handleDiscountSubmit = useCallback(async (code) => {
    try {
      await applyDiscount(code);
      setDiscountError({ code: '', message: '' });
    } catch {
      setDiscountError({ code, message: 'OGILTIG KOD' });
    }
  }, [applyDiscount]);

  const handleDiscountRemove = useCallback(async () => {
    await removeDiscount();
    setDiscountError({ code: '', message: '' });
  }, [removeDiscount]);

  const handleCheckout = useCallback(() => {
    navigate('/clothing/cls-se/checkout');
  }, [navigate]);

  /* ── Edit item configuration ──
   * Reopens the configurator pre-populated with the line's options
   * (location.state.editCart, mirroring the edit-order flow). Saving there
   * replaces the cart line (remove + re-add, server re-prices). Variation
   * lines reopen their school-shop variation route so shop/class overrides
   * apply; the ?shop from the stored selection pins the exact shop. */
  const { shopSlug: storedShopSlug } = useSchoolSelection('clsse');
  const handleEditProduct = useCallback((p) => {
    const opts = p.options || {};
    const editCart = { itemId: p.id, options: opts, quantity: p.quantity };
    if (opts.collectionItemId && opts.schoolSlug) {
      const q = storedShopSlug ? `?shop=${encodeURIComponent(storedShopSlug)}` : '';
      navigate(`/clothing/cls-se/school-shop/${opts.schoolSlug}/product/${opts.collectionItemId}${q}`, { state: { editCart } });
    } else if (opts.slug) {
      // Bare line: keep the collection context so the edit reopens the same
      // variation (and collection-link origin) it was bought from.
      const qs = new URLSearchParams();
      if (opts.collectionItemId) qs.set('variation', opts.collectionItemId);
      if (opts.collectionSlug)   qs.set('collection', opts.collectionSlug);
      const q = qs.toString();
      navigate(`/clothing/cls-se/product-details/${encodeURIComponent(opts.slug)}${q ? `?${q}` : ''}`, { state: { editCart } });
    }
  }, [navigate, storedShopSlug]);

  const handleEditPackage = useCallback((pkg) => {
    const opts = pkg.options || {};
    if (!opts.slug) return;
    const editCart = {
      itemId: pkg.id,
      includedProducts: opts.includedProducts || pkg.includedProducts || [],
      options: opts,
      quantity: pkg.quantity,
    };
    navigate(`/clothing/cls-se/product-package/${encodeURIComponent(opts.slug)}`, { state: { editCart } });
  }, [navigate]);

  /* ── Sidebar: sticky + mobile floating bar ── */
  const { sidebarRef, canStick, showMobileBar } = useCartSidebar([discount]);

  /* ── Set brand ── */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  // Overall orders carry no edit/cancel window — hide the KÖP RISKFRITT
  // promise when the cart holds any overall (standalone or inside a package).
  const hasOveralls = products.some((p) => /overall/i.test(`${p.slug || ''} ${p.name || ''}`))
    || packages.some((pk) => (pk.includedProducts || []).some((ip) => /overall/i.test(ip.productName || '')));

  return (
    <div className="shopping-cart-page">
      <Header mode="default" showTopBar={false} />

      {!loading && products.length === 0 && packages.length === 0 ? (
        <main className="shopping-cart-page__main">
          <EmptyState
            icon={ShoppingBag}
            title="Din varukorg är tom"
            message="Det verkar som att du inte har lagt till något ännu. Utforska vår kollektion och hitta det perfekta."
            actions={[{ label: 'Utforska kollektion', variant: 'main', size: 'secondary', iconRight: <ArrowRight size={20} />, onClick: () => window.location.href = publicHref('/clothing/cls-se') }]}
          />
        </main>
      ) : (
      <>
      <main className="shopping-cart-page__main">
        <div className="shopping-cart-page__container">
          {/* ── Cart details (left on desktop) ── */}
          <div className="shopping-cart-page__details">
            {/* Products section */}
            {products.length > 0 && (
              <section className="shopping-cart-page__section">
                <div className="shopping-cart-page__section-inner">
                  <h2 className="shopping-cart-page__section-title typo-h3">
                    Produkter
                  </h2>
                  <div className="shopping-cart-page__products">
                    {products.map((p) => (
                      <SimpleProduct
                        key={p.id}
                        image={p.options?.image || p.image}
                        imageAlt={p.name}
                        title={p.name}
                        unitPrice={p.unitPrice}
                        priceSuffix=":-"
                        options={(p.attributes && p.attributes.length
                          ? p.attributes
                          : [
                              p.options?.color && { attribute: 'Color', value: p.options.color },
                              p.options?.size && { attribute: 'Size', value: p.options.size },
                            ].filter(Boolean)
                        ).map((a) => ({
                          label: a.attribute === 'Color' ? 'Färg' : a.attribute === 'Size' ? 'Storlek' : a.attribute,
                          value: a.value,
                        }))}
                        quantity={p.quantity}
                        onQuantityChange={(q) => updateItem(p.id, q)}
                        onEdit={(p.options?.slug || p.options?.collectionItemId) ? () => handleEditProduct(p) : undefined}
                        onRemove={() => removeItem(p.id)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Packages section */}
            {packages.length > 0 && (
              <section className="shopping-cart-page__section">
                <div className="shopping-cart-page__section-inner">
                  <h2 className="shopping-cart-page__section-title typo-h3">
                    Paket
                  </h2>
                  <div className="shopping-cart-page__packages">
                    {packages.map((pkg) => (
                      <PackageProduct
                        key={pkg.id}
                        image={pkg.options?.image || pkg.image}
                        imageAlt={pkg.name}
                        title={pkg.name}
                        unitPrice={pkg.unitPrice}
                        priceSuffix=":-"
                        quantity={pkg.quantity}
                        onQuantityChange={(q) => updateItem(pkg.id, q)}
                        onEdit={pkg.options?.slug ? () => handleEditPackage(pkg) : undefined}
                        onRemove={() => removeItem(pkg.id)}
                        includedTitle="INKLUDERADE PRODUKTER"
                        includedProducts={(pkg.options?.includedProducts || pkg.includedProducts || []).map((ip) => ({
                          image: ip.image || null,
                          imageAlt: ip.productName || ip.title || '',
                          title: ip.productName || ip.title || ip.groupName,
                          subtitle: [
                            [ip.color, ip.size].filter(Boolean).join(', '),
                            ...(ip.attributesList || []).map((a) => `${a.label}: ${a.value}`),
                          ].filter(Boolean),
                        }))}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* ── Continue shopping — back to the customer's school shop.
                 The bare /school-shop route resolves the stored selection
                 (or falls back to the landing when none). ── */}
            <div className="shopping-cart-page__continue pt-medium-l">
              <Button
                size="secondary"
                variant="outline"
                iconLeft={<ArrowLeft size={20} />}
                onClick={() => navigate('/clothing/cls-se/school-shop')}
              >
                Fortsätt handla
              </Button>
            </div>
          </div>

          {/* ── Summary sidebar ── */}
          <aside
            ref={sidebarRef}
            className={`shopping-cart-page__sidebar${canStick ? ' shopping-cart-page__sidebar--sticky' : ''}`}
          >
            <Summary
              title="Sammanfattning"
              showDelivery={false}
              deliveryTime="3 Veckor"
              deliveryCost="Gratis"
              deliveryDescription="Leverans till din skola"
              deliveryPrice="0:-"
              lineItems={[
                ...(productsTotal > 0 ? [{ label: 'Produkter', price: `${Math.round(productsTotal)}:-` }] : []),
                ...(packagesTotal > 0 ? [{ label: 'Paket', price: `${Math.round(packagesTotal)}:-` }] : []),
              ]}
              discounts={discount
                ? [{ label: discountText, amount: `-${Math.round(discountAmount)}:-` }]
                : []
              }
              totalLabel="Totalt"
              totalPrice={`${Math.round(total)}:-`}
              originalPrice={discount ? `${Math.round(subtotal)}:-` : ''}
              ctaLabel="Till kassan"
              onCtaClick={handleCheckout}
              promoTitle="KÖP RISKFRITT"
              promoSubtitle="Beställ idag. Bestäm dig imorgon. Du har till webbshopens stängningsdatum på dig att göra ändringar eller ångra ditt köp."
              showPromo={!hasOveralls}
              showDiscountCode
              discountCodeLabel="Rabattkod"
              discountCode={discount?.code || ''}
              discountSubmitted={!!discount}
              discountLabel={discountText}
              discountErrorCode={discountError.code}
              discountErrorMessage={discountError.message}
              onDiscountSubmit={handleDiscountSubmit}
              onDiscountRemove={handleDiscountRemove}
            />
          </aside>
        </div>
      </main>

      {/* ── Mobile floating checkout bar ── */}
      <div
        className={`shopping-cart-page__mobile-bar${showMobileBar ? ' shopping-cart-page__mobile-bar--visible' : ''}`}
      >
        <Button
          size="secondary"
          variant="alternative"
          iconRight={<ArrowRight size={20} />}
          onClick={handleCheckout}
          className="shopping-cart-page__mobile-bar-btn"
        >
          {`Till kassan \u2022 ${Math.round(total)}:-`}
        </Button>
      </div>
      </>
      )}

      <Footer />
    </div>
  );
};

export default ShoppingCart;
